/**
 * contextoDeVista — qué reunión se está mirando, a prueba de recargas.
 *
 * El hash dice la pantalla (`#v2/reunion`) pero no CUÁL reunión: eso vivía solo
 * en memoria. Al recargar con F5 el estado volvía a cero y el detalle se abría
 * sin reunión, sin minuta y sin grabación.
 *
 * Y lo peor no era quedarse vacío, sino lo que decía entonces: «No se encontró
 * grabación de esta reunión en tu OneDrive». Es una respuesta falsa a una
 * pregunta que no llegó a hacerse —no se buscó ninguna grabación, porque no se
 * sabía de qué reunión—, y quien la leía se iba a buscar su grabación a mano.
 *
 * Se guarda en `sessionStorage` y no en `localStorage` a propósito: es de esta
 * pestaña y de este rato. Otra pestaña puede estar en otra reunión, y mañana no
 * hay por qué volver a la de hoy.
 */

export const LLAVE_CONTEXTO = 'minutas-v2-contexto';

export const VACIO = { reunion: null, archivo: null, minuteId: null, grabacion: null };

// Las fechas salen a JSON como texto y vuelven como texto. La pantalla de
// detalle resta `fin - inicio` y llama a `.getTime()`, así que sin devolverlas
// a ser fechas la reunión volvería con una duración de NaN —y un NaN no se ve:
// se cuela hasta la pantalla como un hueco—.
//
// Se reviven por su forma y no por su nombre: las hay dentro de la cita y
// dentro de la grabación, con nombres distintos (`inicio`, `fin`, `creada`), y
// enumerarlos a mano sería olvidarse de uno.
const ES_FECHA_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

export function revivir(valor) {
  if (typeof valor === 'string') return ES_FECHA_ISO.test(valor) ? new Date(valor) : valor;
  if (Array.isArray(valor)) return valor.map(revivir);
  if (valor && typeof valor === 'object') {
    const salida = {};
    for (const [clave, dentro] of Object.entries(valor)) salida[clave] = revivir(dentro);
    return salida;
  }
  return valor;
}

/** Lo último que se estaba mirando en esta pestaña, si se puede recuperar. */
export function contextoGuardado() {
  try {
    const crudo = window.sessionStorage.getItem(LLAVE_CONTEXTO);
    if (!crudo) return { ...VACIO };
    const leido = revivir(JSON.parse(crudo));
    // Lo guardado manda sobre los huecos, pero nunca añade campos que no
    // existan: un `sessionStorage` de una versión anterior no debe colar
    // propiedades que la pantalla ya no entiende.
    return { ...VACIO, ...leido, archivo: null };
  } catch {
    // Sin sessionStorage —ventana privada, cookies bloqueadas— se sigue como
    // antes: la recarga pierde el contexto, pero la pantalla ya no lo disimula.
    return { ...VACIO };
  }
}

/**
 * Apunta lo que se está mirando.
 *
 * `archivo` se queda fuera: es un File del navegador, no sobrevive a JSON y no
 * tiene por qué —quien recargue tendrá que volver a elegirlo—. Guardarlo como
 * `{}` sería peor que no guardarlo: la pantalla creería tener un archivo vacío.
 */
export function guardarContexto(contexto) {
  try {
    const { archivo, ...guardable } = contexto || {};
    window.sessionStorage.setItem(LLAVE_CONTEXTO, JSON.stringify(guardable));
    return true;
  } catch {
    return false;
  }
}

const contextoDeVista = { contextoGuardado, guardarContexto, revivir, VACIO, LLAVE_CONTEXTO };
export default contextoDeVista;
